import React, { useRef, useState } from 'react';
import {
  ScrollView, StyleSheet, Text, View, ActivityIndicator, Image, TextInput, Modal, Platform,
  KeyboardAvoidingView, StatusBar,
  Linking,
} from 'react-native';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import {
  CameraView, PermissionResponse, useCameraPermissions,
} from 'expo-camera';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { initialWindowMetrics, useSafeAreaInsets } from 'react-native-safe-area-context';
import { manipulateAsync } from 'expo-image-manipulator';
import DeviceInfo from 'react-native-device-info';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import createTicket from '../../requests/Tickets/CreateTicket';
import IssueReported from '../60pxIcons/IssueReported';
import AlertModal from '../AlertModal';
import CloseModalIcon from '../CloseModalIcon';
import Style from '../../constants/Style';
import CameraIcon from '../24pxIcons/CameraIcon';
import isMobile from '../IsMobile';
import { Template, TemplateIssue } from '../../constants/Types';
import PressableStyled from '../PressableStyled';
import Button from '../Button';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import {
  DEFAULT_TEMPLATE_TITLE, getFormattedTemplateSubtitle, getFormattedTemplateTitle, openInNewTab,
} from '../../constants/Utils';

interface ReportIssueModalProps {
  isVisible: boolean;
  hide: () => void;
  assetName: string;
  solveTagId: string;
  template: Template;
  refresh: () => void;
}

const ReportTicketModal = ({
  isVisible, hide, template, assetName, solveTagId, refresh,
}: ReportIssueModalProps) => {
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const [selectedIssueId, setSelectedIssueId] = useState<string>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [reportedTicketId, setReportedTicketId] = useState<string | undefined>(undefined);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [cameraPermissionAlertVisible,
    setCameraPermissionAlertVisible] = useState<boolean>(false);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [cameraReady, setCameraReady] = useState<boolean>(false);
  const [status, requestPermission] = useCameraPermissions();
  const [image, setImage] = useState<string>(null);
  const [capturing, setCapturing] = useState<boolean>(false);
  const [otherIssueText, setOtherIssueText] = useState<string>('');
  const cameraRef = useRef<CameraView>(null);
  const isFocused: boolean = useIsFocused();
  const navigation = useNavigation();
  const subtitle: string = getFormattedTemplateSubtitle(template?.subtitle);
  const issues: TemplateIssue[] = template?.issues ?? [];

  const onModalHide: () => void = () => {
    setLoading(false);
    setReportedTicketId(undefined);
    setSelectedIssueId(null);
    setImage(null);
    setOtherIssueText('');
  };

  const showError: () => void = () => {
    setLoading(false);
    setAlertVisible(true);
  };

  const reportIssueButtonPress: () => void = async () => {
    setLoading(true);
    const selectedIssue = issues.find((issue: TemplateIssue) => issue.Id === selectedIssueId);
    if (!selectedIssue) {
      setLoading(false);
      return;
    }

    let title: string = selectedIssue.Text;
    if (title.toLowerCase().trim() === 'other') title = otherIssueText;
    const uniqueDeviceId: string = await DeviceInfo.getUniqueId();
    createTicket({
      solveTagId,
      issue: title,
      issueId: selectedIssueId,
      base64Image: image,
      userDeviceId: isMobile ? uniqueDeviceId : '',
    }).then((res) => {
      if (res && !res.isError) {
        setReportedTicketId(res.ticketId ?? res.ticket?.id);
        refresh();
      } else showError();
      setLoading(false);
    }).catch(showError);
  };

  const openCamera: () => void = () => {
    if (!status || (!status.granted && status.canAskAgain)) {
      requestPermission().then((permission: PermissionResponse) => {
        if (permission?.granted) setCameraOpen(true);
        else setCameraPermissionAlertVisible(true);
      });
    } else if (status && status.granted) setCameraOpen(true);
    else setCameraPermissionAlertVisible(true);
  };

  const openSubtitleUrl: () => void = () => {
    openInNewTab(template.subtitleUrl);
  };

  const handleImage = (uri: string) => {
    Image.getSize(uri, (_, h) => {
      manipulateAsync(uri, [{ resize: { height: Math.min(h, 1080) } }], {
        base64: true,
        compress: 0.3,
      }).then((m) => {
        setImage(`data:image/jpeg;base64,${m.base64}`);
      });
    });
  };

  const navigateToTicket = async () => {
    const clientId = await AsyncStorage.getItem('clientName');
    navigation.navigate(AppScreen.TICKETS as any, {
      reportId: reportedTicketId,
      buildingId,
      clientId: clientId ?? undefined,
    });
    hide();
  };

  const insets: { top: number; bottom: number } = useSafeAreaInsets();
  const modalTopInset = Platform.OS === 'android' ? Math.max(
    insets.top,
    initialWindowMetrics?.insets.top ?? 0,
    StatusBar.currentHeight ?? 0,
    32,
  ) : insets.top;
  const modalBottomInset = Platform.OS === 'android' ? Math.max(
    insets.bottom,
    initialWindowMetrics?.insets.bottom ?? 0,
    24,
  ) : insets.bottom;

  const otherIssue: TemplateIssue | undefined = issues.find(
    (issue: TemplateIssue) => issue.Text?.toLowerCase() === 'other' && issue.Enabled !== false,
  );

  const submitDisabled: boolean = !selectedIssueId
    || loading
    || (selectedIssueId === otherIssue?.Id && !(otherIssueText?.length > 0));

  const testIDNames: string[] = ['first', 'second', 'third', 'fourth', 'fifth'];

  if (!template || !assetName || !solveTagId) return null;

  return (
    <Modal
      {...{ onModalHide }}
      visible={isVisible}
      onRequestClose={hide}
      style={Style.modal}
      transparent={true}
      statusBarTranslucent={false}
    >
      <KeyboardAvoidingView
        style={{ width: '100%', flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'position' : 'padding'}
      >
        <ScrollView style={{ height: '100%', backgroundColor: '#00000030' }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}>
          <View
            style={[
              Style.modalContent,
              styles.container,
              {
                paddingTop: modalTopInset,
                paddingBottom: modalBottomInset,
              },
            ]}
          >
            {reportedTicketId ? (
              <View
                testID="successfully-reported-ticket-modal"
                style={styles.successContent}
              >
                <View>
                  <IssueReported style={styles.issueReportedImage} />
                  <Text style={styles.successTitle}>
                    {"Someone's coming to resolve your report"}
                  </Text>
                </View>
                <Text style={styles.successTitle}>
                  {'We will notify you once the issue is fixed'}
                </Text>
                <Text style={styles.successDescription}>
                  {
                    'Thank you for reporting!\n\n\nKeep reporting and make your workplace a better place'
                  }
                </Text>
                <Button
                  affirmative
                  title="View reported ticket"
                  onPress={navigateToTicket}
                  style={styles.navigateToTicketButton}
                />
              </View>
            ) : (
              <>
                <Text style={styles.title}>
                  {getFormattedTemplateTitle(
                    template?.title
                    ?? DEFAULT_TEMPLATE_TITLE,
                    assetName,
                  )}
                </Text>
                {template.subtitleUrl ? (
                  <PressableStyled onPress={openSubtitleUrl}>
                    <Text style={[styles.subTitle, styles.subtitleUrl]}>
                      {subtitle}
                    </Text>
                  </PressableStyled>
                ) : (
                  <Text style={styles.subTitle}>{subtitle}</Text>
                )}
                <ScrollView
                  contentContainerStyle={styles.scrollViewContent}
                  keyboardShouldPersistTaps="handled"
                >
                  <>
                    {issues
                      ?.filter(
                        (issue: TemplateIssue) => issue.Enabled !== false
                          && issue.Text?.toLowerCase() !== 'other',
                      )
                      ?.map((issue: TemplateIssue, index: number) => (
                        <View style={styles.issue} key={`Issue_${issue.Id}`}>
                          <PressableStyled
                            style={styles.issueContent}
                            disabled={loading}
                            onPress={() => setSelectedIssueId(
                              selectedIssueId === issue.Id
                                ? undefined
                                : issue.Id,
                            )
                            }
                            testID={`am-select-${testIDNames[index]}-issue-btn`}
                          >
                            <View
                              style={[
                                styles.issueCircle,
                                selectedIssueId === issue.Id
                                && styles.issueCircleSelected,
                              ]}
                            />
                            <Text style={styles.issueText}>{issue.Text}</Text>
                          </PressableStyled>
                        </View>
                      ))}
                    {otherIssue ? (
                      <View style={styles.issue} key={`Issue_${otherIssue.Id}`}>
                        <PressableStyled
                          style={styles.issueContent}
                          disabled={loading}
                          onPress={() => setSelectedIssueId(
                            selectedIssueId === otherIssue.Id
                              ? undefined
                              : otherIssue.Id,
                          )
                          }
                        >
                          <View
                            style={[
                              styles.issueCircle,
                              selectedIssueId === otherIssue.Id
                              && styles.issueCircleSelected,
                            ]}
                          />
                          <Text style={styles.issueText}>
                            {otherIssue.Text}
                          </Text>
                        </PressableStyled>
                        <TextInput
                          placeholder="Type your issue here..."
                          placeholderTextColor={Colors.light.hereworksPrimaryGrey}
                          style={styles.textInput}
                          value={otherIssueText}
                          onChangeText={(text: string) => {
                            setSelectedIssueId(otherIssue.Id);
                            setOtherIssueText(text);
                          }}
                          multiline
                        />
                      </View>
                    ) : null}
                    {image ? (
                      <View>
                        <Image
                          style={styles.imagePreview}
                          resizeMode="cover"
                          source={{ uri: image }}
                        />
                        <PressableStyled
                          style={[Style.closeButton, styles.imageCloseButton]}
                          onPress={() => setImage(null)}
                        >
                          <CloseModalIcon />
                        </PressableStyled>
                      </View>
                    ) : null}
                  </>
                </ScrollView>
                {isMobile && !template.disablePhotoUpload && <PressableStyled
                  onPress={openCamera}
                  style={[
                    styles.button,
                    styles.takePhotoButton,
                    (!selectedIssueId || loading) && styles.buttonDisabled,
                  ]}
                  disabled={!selectedIssueId || loading}
                >
                  <CameraIcon />
                  <Text style={[styles.buttonText, styles.takePhotoText]}>
                    {'Take a photo'}
                  </Text>
                </PressableStyled>}
                <PressableStyled
                  onPress={reportIssueButtonPress}
                  style={[
                    styles.button,
                    styles.reportButton,
                    submitDisabled && styles.buttonDisabled,
                  ]}
                  disabled={submitDisabled}
                  testID="report-an-issue-btn"
                >
                  <Text style={styles.buttonText}>{'Report a ticket'}</Text>
                </PressableStyled>
              </>
            )}
            <PressableStyled
              onPress={() => {
                onModalHide();
                hide();
              }}
              style={[Style.closeButton, { top: 12 + modalTopInset, end: 12 }]}
              testID={
                reportedTicketId
                  ? 'close-successfully-reported-ticket-modal-button'
                  : 'am-close-report-issue-modal-btn'
              }
            >
              <CloseModalIcon />
            </PressableStyled>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
      {isMobile && <Modal
        style={Style.modal}
        visible={cameraOpen}
        statusBarTranslucent={false}
      >
        {status?.granted && isFocused && (
          <CameraView
            ratio="16:9"
            style={styles.cameraWindow}
            ref={cameraRef}
            onCameraReady={() => setCameraReady(true)}
            onMountError={() => { }}
          >
            {cameraReady && (
              <PressableStyled
                style={[styles.captureButton, { marginBottom: 20 + modalBottomInset }]}
                onPress={() => {
                  setCapturing(true);
                  cameraRef.current
                    .takePictureAsync({ base64: true })
                    .then((picture) => {
                      handleImage(`data:image/jpeg;base64,${picture.base64}`);
                      setCameraOpen(false);
                      setCapturing(false);
                    });
                }}
              >
                <View style={styles.captureButtonCircle} />
                {capturing && (
                  <ActivityIndicator style={{ position: 'absolute' }} />
                )}
              </PressableStyled>
            )}
            <PressableStyled
              onPress={() => setCameraOpen(false)}
              style={[Style.closeButton, { top: 12 + modalTopInset, end: 12 }]}
            >
              <CloseModalIcon />
            </PressableStyled>
          </CameraView>
        )}
      </Modal>}
      <AlertModal
        title="Error"
        message="Unable to report issue right now. Please try again later."
        error
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
      />
      <AlertModal
        modalVisible={cameraPermissionAlertVisible}
        setModalVisible={setCameraPermissionAlertVisible}
        title="Camera permission denied"
        message={'Please allow camera access in your device settings to take a photo.'}
        error
        actionTitle="Settings"
        cancelTitle="Close"
        action={() => {
          Linking.openSettings().catch(() => { });
        }}
      />
      <ActivityIndicator
        size="large"
        style={Style.activityIndicator}
        animating={loading}
      />
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    height: isMobile ? '100%' : 600,
    width: isMobile ? '100%' : 500,
    alignSelf: 'center',
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: isMobile ? undefined : 10,
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
  scrollViewContent: {
    paddingHorizontal: 24,
  },
  title: {
    fontSize: 24,
    lineHeight: 34,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksPrimaryGrey,
    textAlign: 'center',
    marginHorizontal: 44,
    marginTop: 32,
  },
  subTitle: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksPrimaryGrey800,
    textAlign: 'center',
    marginBottom: 20,
    marginHorizontal: 45,
  },
  issue: {
    padding: 10,
    marginBottom: 10,
    borderRadius: 12,
    backgroundColor: '#FFF',
  },
  issueContent: {
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
  },
  issueCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.light.hereworksPaleBlue800,
    marginEnd: 10,
  },
  issueCircleSelected: {
    backgroundColor: Colors.light.hereworksYellow500,
  },
  issueText: {
    fontSize: 18,
    lineHeight: 27,
    color: '#000',
    fontFamily: 'Poppins_500Medium',
    flex: 1,
  },
  button: {
    justifyContent: 'center',
    alignItems: 'center',
    height: 52,
    borderRadius: 12,
    marginTop: 12,
    marginHorizontal: 24,
  },
  takePhotoButton: {
    borderWidth: 2,
    borderColor: Colors.light.hereworksBlue500,
    flexDirection: 'row',
  },
  reportButton: {
    backgroundColor: Colors.light.hereworksBlue500,
    marginBottom: Platform.OS === 'android' ? 12 : 33,
  },
  buttonText: {
    fontSize: 16,
    lineHeight: 24,
    color: '#FFF',
    fontFamily: 'Poppins_500Medium',
    textAlign: 'center',
  },
  takePhotoText: {
    color: Colors.light.hereworksBlue500,
    marginStart: 12,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  issueReportedImage: {
    alignSelf: 'center',
    marginTop: 36,
    marginBottom: 20,
  },
  successContent: {
    justifyContent: 'space-evenly',
    alignItems: 'center',
    paddingBottom: 24,
  },
  successTitle: {
    fontSize: 24,
    lineHeight: 36,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
    marginHorizontal: 44,
    marginTop: 20,
  },
  successDescription: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_500Medium',
    textAlign: 'center',
    marginHorizontal: 60,
    marginTop: 10,
  },
  cameraWindow: {
    flex: 1,
    margin: 0,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  captureButton: {
    borderWidth: 6,
    borderColor: '#fff',
    borderRadius: 35,
    width: 70,
    height: 70,
    backgroundColor: 'transparent',
    padding: 8,
    marginBottom: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  captureButtonCircle: {
    backgroundColor: '#fff',
    width: 50,
    height: 50,
    borderRadius: 25,
  },
  imagePreview: {
    width: '100%',
    aspectRatio: 1.17,
    borderRadius: 12,
    marginBottom: 12,
  },
  imageCloseButton: {
    top: 12,
    end: 12,
  },
  textInput: {
    marginVertical: 12,
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksPrimaryGrey900,
    padding: 12,
  },
  navigateToTicketButton: isMobile
    ? {
      marginTop: 20,
      flex: undefined,
    }
    : {
      marginTop: 20,
    },
  subtitleUrl: {
    textDecorationLine: 'underline',
  },
});

export default ReportTicketModal;
